/**
 * React Query hooks for individual QMS Tickets
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchQmsTicket,
  createQmsTicket,
  updateQmsTicket,
  qdApproveTicket,
  ceoReviewTicket,
  searchLinkableTickets,
  linkQmsTicket,
  unlinkQmsTicket,
  createCapaFromTicket,
  escalateToCeo,
  submitForApproval,
  applyQmsWorkflowAction,
} from '../api/qmsApi';
import type {
  QmsTicketPayload,
  QmsQdApprovalPayload,
  QmsCeoReviewPayload,
  QmsCreateCapaPayload,
  QmsTicket,
  QmsWorkflowActionPayload,
} from '../types/qms.types';
import { qmsKeys } from './useQmsBoard';

/**
 * Fetch a single QMS ticket with populated fields
 */
export function useQmsTicket(
  ticketId: string | null,
  options?: { enabled?: boolean }
) {
  const populateFields = [
    'assigned_to',
    'created_by',
    'related_tickets',
    'qd_approved_by',
    'ceo_reviewed_by',
    'validated_by',
    'intake_decision_by',
    'change_approved_by',
    'parent_ticket',
  ];

  return useQuery({
    queryKey: qmsKeys.ticket(ticketId!),
    queryFn: () => fetchQmsTicket(ticketId!, populateFields),
    enabled: !!ticketId && (options?.enabled !== false),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Create a new QMS ticket
 */
export function useCreateQmsTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: QmsTicketPayload) => createQmsTicket(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.boards() });
      queryClient.invalidateQueries({ queryKey: qmsKeys.tickets() });
    },
  });
}

/**
 * Update an existing QMS ticket
 */
export function useUpdateQmsTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ticketId,
      payload,
    }: {
      ticketId: string;
      payload: Partial<QmsTicketPayload>;
    }) => updateQmsTicket(ticketId, payload),
    onSuccess: (_, variables) => {
      // Invalidate the specific ticket and all boards
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(variables.ticketId) });
      queryClient.invalidateQueries({ queryKey: qmsKeys.boards() });
      // Invalidate timeline to show field update activity
      queryClient.invalidateQueries({ queryKey: qmsKeys.timeline(variables.ticketId) });
    },
  });
}

export function useQmsWorkflowAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ticketId,
      payload,
    }: {
      ticketId: string;
      payload: QmsWorkflowActionPayload;
    }) => applyQmsWorkflowAction(ticketId, payload),
    onSuccess: (ticket, variables) => {
      queryClient.setQueryData(qmsKeys.ticket(variables.ticketId), ticket);
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(variables.ticketId) });
      queryClient.invalidateQueries({ queryKey: qmsKeys.boards() });
      queryClient.invalidateQueries({ queryKey: qmsKeys.timeline(variables.ticketId) });
    },
  });
}

/**
 * QD Approval action
 */
export function useQdApproval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ticketId,
      payload,
    }: {
      ticketId: string;
      payload: QmsQdApprovalPayload;
    }) => qdApproveTicket(ticketId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(variables.ticketId) });
      queryClient.invalidateQueries({ queryKey: qmsKeys.boards() });
    },
  });
}

/**
 * CEO Review action
 */
export function useCeoReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ticketId,
      payload,
    }: {
      ticketId: string;
      payload: QmsCeoReviewPayload;
    }) => ceoReviewTicket(ticketId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(variables.ticketId) });
      queryClient.invalidateQueries({ queryKey: qmsKeys.boards() });
    },
  });
}

/**
 * Search for linkable tickets
 */
export function useSearchLinkableTickets(
  organisation: string,
  searchTerm: string,
  excludeTicketId?: string,
  excludeLinkedIds?: string[]
) {
  return useQuery<QmsTicket[]>({
    queryKey: ['qms', 'search', 'linkable', organisation, searchTerm, excludeTicketId, excludeLinkedIds],
    queryFn: () => searchLinkableTickets(organisation, searchTerm, excludeTicketId, excludeLinkedIds),
    enabled: !!organisation && searchTerm.length >= 2,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Link a ticket to another ticket
 */
export function useLinkQmsTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ticketId,
      relatedTicketId,
      relationship,
    }: {
      ticketId: string;
      relatedTicketId: string;
      relationship: string;
    }) => linkQmsTicket(ticketId, relatedTicketId, relationship),
    onSuccess: (_, variables) => {
      // Invalidate both tickets
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(variables.ticketId) });
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(variables.relatedTicketId) });
      // Invalidate boards
      queryClient.invalidateQueries({ queryKey: qmsKeys.boards() });
    },
  });
}

/**
 * Unlink a ticket from another ticket
 */
export function useUnlinkQmsTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ticketId,
      relatedTicketId,
    }: {
      ticketId: string;
      relatedTicketId: string;
    }) => unlinkQmsTicket(ticketId, relatedTicketId),
    onSuccess: (_, variables) => {
      // Invalidate both tickets
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(variables.ticketId) });
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(variables.relatedTicketId) });
      // Invalidate boards
      queryClient.invalidateQueries({ queryKey: qmsKeys.boards() });
    },
  });
}

/**
 * Create a CAPA from an existing ticket
 */
export function useCreateCapaFromTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ticketId,
      payload,
    }: {
      ticketId: string;
      payload: QmsCreateCapaPayload;
    }) => createCapaFromTicket(ticketId, payload),
    onSuccess: (_, variables) => {
      // Invalidate the parent ticket
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(variables.ticketId) });
      // Invalidate boards to show the new CAPA
      queryClient.invalidateQueries({ queryKey: qmsKeys.boards() });
      queryClient.invalidateQueries({ queryKey: qmsKeys.tickets() });
    },
  });
}

/**
 * Escalate a QD-approved ticket to CEO review
 */
export function useEscalateToCeo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ticketId: string) => escalateToCeo(ticketId),
    onSuccess: (_, ticketId) => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(ticketId) });
      queryClient.invalidateQueries({ queryKey: qmsKeys.boards() });
    },
  });
}

/**
 * Submit (or resubmit) a ticket for QD approval
 */
export function useSubmitForApproval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ticketId: string) => submitForApproval(ticketId),
    onSuccess: (_, ticketId) => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.ticket(ticketId) });
      queryClient.invalidateQueries({ queryKey: qmsKeys.boards() });
    },
  });
}
